import { Button } from '@app/components/various/Button';
import { ChevronLeft, DoubleChevronRight, ShoppingBag, SuccessCircleOutline, Tag } from '@app/components/various/Icons';
import { getOrderDetails } from '@app/ducks';
import { compiledPaths } from '@app/paths';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useAllProductsLink } from '@app/utils/hooks';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { isEmpty } from '@app/utils/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

const PRIMARY_COMPACT_BREAKPOINT = 792;
const SECONDARY_COMPACT_BREAKPOINT = 672;

interface Props {
  location: 'products' | 'selection' | 'page';
  parentWidth: number;
  orderId: string | undefined;
}

export const OrderActions = ({ location, parentWidth, orderId }: Props) => {
  const { t } = useTranslation(['selections', 'common', 'products']);

  const orderDetails = useSelector(getOrderDetails);
  const { hasAnyOrderData } = useAvailableOrderDetails();
  const allProductsLink = useAllProductsLink(orderId);
  const { trackEvent } = useAmplitude();

  const hasProducts = !isEmpty(orderDetails.order.products);

  const primaryActionPath = React.useMemo(() => {
    if (!hasAnyOrderData) {
      return '';
    }

    switch (location) {
      case 'products':
        return compiledPaths.ORDER_DETAILS({ id: orderId });
      case 'selection':
        return compiledPaths.CHECKOUT_ORDER({ id: orderId });
      case 'page':
        return allProductsLink;
      default:
        return '';
    }
  }, [allProductsLink, hasAnyOrderData, location, orderId]);

  const secondaryActionPath = React.useMemo(() => {
    if (!hasAnyOrderData) {
      return '';
    }

    switch (location) {
      case 'products':
        return compiledPaths.CHECKOUT_ORDER({ id: orderId });
      case 'selection':
        return allProductsLink;
      case 'page':
        return compiledPaths.ORDER_DETAILS({ id: orderId });
      default:
        return '';
    }
  }, [allProductsLink, hasAnyOrderData, location, orderId]);

  const primaryActionIcon = React.useMemo(() => {
    if (parentWidth <= PRIMARY_COMPACT_BREAKPOINT) {
      return undefined;
    }

    switch (location) {
      case 'products':
        return ShoppingBag;
      case 'selection':
        return SuccessCircleOutline;
      case 'page':
        return Tag;
      default:
        return undefined;
    }
  }, [location, parentWidth]);

  const secondaryActionIcon = React.useMemo(() => {
    if (parentWidth > SECONDARY_COMPACT_BREAKPOINT) {
      return undefined;
    }

    switch (location) {
      case 'products':
        return DoubleChevronRight;
      case 'selection':
        return ChevronLeft;
      case 'page':
        return ShoppingBag;
      default:
        return undefined;
    }
  }, [location, parentWidth]);

  const primaryActionLabel = React.useMemo(() => {
    switch (location) {
      case 'products':
        return t('selections:selection_one');
      case 'selection':
        return t('common:checkout');
      case 'page':
        return t('products:all_products');
      default:
        return '';
    }
  }, [location, t]);

  const secondaryActionLabel = React.useMemo(() => {
    switch (location) {
      case 'products':
        return t('common:checkout');
      case 'selection':
        return t('products:all_products');
      case 'page':
        return t('selections:selection_one');
      default:
        return '';
    }
  }, [location, t]);

  const primaryActionDataTestId = React.useMemo(() => {
    switch (location) {
      case 'products':
      case 'page':
        return 'selectionStep';
      case 'selection':
        return 'checkoutStep';
      default:
        return '';
    }
  }, [location, t]);

  const secondaryActionDataTestId = React.useMemo(() => {
    switch (location) {
      case 'products':
      case 'page':
        return 'checkoutStep';
      case 'selection':
        return 'productsStep';
      default:
        return '';
    }
  }, [location]);

  const handlePrimaryClick = () => {
    if (location === 'selection') {
      trackEvent({ name: 'bottomBar.button.navigatetocheckout.click' });
    }
  };

  const handleSecondaryClick = () => {
    if (location === 'products') {
      trackEvent({ name: 'bottomBar.button.navigatetocheckout.click' });
    }
  };

  const shouldShowSecondaryActionLabel = React.useMemo(() => parentWidth > SECONDARY_COMPACT_BREAKPOINT, [parentWidth]);

  return (
    <div className="flex gap-2">
      <Button
        as={Link}
        disabled={!hasAnyOrderData || (location === 'products' && !hasProducts)}
        dataTestId={secondaryActionDataTestId}
        aria-label={secondaryActionLabel}
        to={secondaryActionPath}
        icon={secondaryActionIcon}
        color="secondary"
        variant="bordered"
        onClick={handleSecondaryClick}
      >
        {shouldShowSecondaryActionLabel && secondaryActionLabel}
      </Button>
      <Button
        as={Link}
        disabled={!hasAnyOrderData || (location === 'selection' && !hasProducts)}
        dataTestId={primaryActionDataTestId}
        to={primaryActionPath}
        icon={primaryActionIcon}
        onClick={handlePrimaryClick}
      >
        {primaryActionLabel}
      </Button>
    </div>
  );
};
